import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    url?: string;
    type?: 'website' | 'article';
    noindex?: boolean;
    // Дополнительные поля для статей
    articlePublishedTime?: string;
    articleModifiedTime?: string;
    articleAuthor?: string;
    articleSection?: string;
    articleTags?: string[];
}

/**
 * Компонент для управления SEO мета-тегами на странице
 * Использует react-helmet-async для динамического обновления head
 */
const SEO: React.FC<SEOProps> = ({
    title = 'CodeReadAI - Изучение программирования через практику',
    description = 'Платформа для изучения программирования с помощью искусственного интеллекта. Анализируйте код, получайте обратную связь от ИИ и улучшайте свои навыки программирования.',
    keywords = 'программирование, обучение, код, ИИ, искусственный интеллект, практика, разработка',
    image = '/logo512.png',
    url = 'https://codereadai.ru',
    type = 'website',
    noindex = false,
    articlePublishedTime,
    articleModifiedTime,
    articleAuthor = 'CodeReadAI',
    articleSection,
    articleTags = [],
}) => {
    const fullTitle = title.includes('CodeReadAI') ? title : `${title} | CodeReadAI`;
    const fullImage = image.startsWith('http') ? image : `https://codereadai.ru${image}`;
    const fullUrl = url.startsWith('http') ? url : `https://codereadai.ru${url}`;
    const baseUrl = 'https://codereadai.ru';

    // Структурированные данные для статей (JSON-LD)
    const articleStructuredData = type === 'article' && articlePublishedTime ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": fullTitle,
        "description": description,
        "image": fullImage,
        "datePublished": articlePublishedTime,
        "dateModified": articleModifiedTime || articlePublishedTime,
        "author": {
            "@type": "Organization",
            "name": articleAuthor
        },
        "publisher": {
            "@type": "Organization",
            "name": "CodeReadAI",
            "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/logo512.png`
            }
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": fullUrl
        },
        ...(articleSection && { "articleSection": articleSection }),
        ...(articleTags.length > 0 && { "keywords": articleTags.join(', ') })
    } : null;

    // Структурированные данные для списка статей (BreadcrumbList)
    const breadcrumbStructuredData = url.includes('/article/') ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Главная",
                "item": baseUrl
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "Статьи",
                "item": `${baseUrl}/articles`
            },
            {
                "@type": "ListItem",
                "position": 3,
                "name": title,
                "item": fullUrl
            }
        ]
    } : null;

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="title" content={fullTitle} />
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            {!noindex && <meta name="robots" content="index, follow" />}
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={fullImage} />
            <meta property="og:locale" content="ru_RU" />
            <meta property="og:site_name" content="CodeReadAI" />
            {type === 'article' && articlePublishedTime && (
                <>
                    <meta property="article:published_time" content={articlePublishedTime} />
                    {articleModifiedTime && (
                        <meta property="article:modified_time" content={articleModifiedTime} />
                    )}
                    {articleAuthor && (
                        <meta property="article:author" content={articleAuthor} />
                    )}
                    {articleSection && (
                        <meta property="article:section" content={articleSection} />
                    )}
                    {articleTags.length > 0 && articleTags.map((tag, index) => (
                        <meta key={index} property="article:tag" content={tag} />
                    ))}
                </>
            )}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={fullUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={fullImage} />

            {/* Canonical URL */}
            <link rel="canonical" href={fullUrl} />

            {/* Структурированные данные (JSON-LD) */}
            {articleStructuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(articleStructuredData)}
                </script>
            )}
            {breadcrumbStructuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbStructuredData)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
